import { z } from 'zod';
import { DocId, IsoTimestamp } from '../common.js';
import { sourceDefaultClassificationFields } from '../classification.js';
/**
 * Jira Cloud ingest sources.
 *
 * One row per selected Jira project. The actual issues land as normal
 * `kennisbank_ring{N}_documents/...` registry rows with filenames
 * `jira-{ISSUE_KEY}.md` (lower-cased) so the existing retrieval pipeline
 * treats them like any other doc — same as Confluence pages. There is no
 * dedicated `kennisbank_jira` collection; provenance is derived from the
 * `jira-` filename prefix + the `jiraSourceId` backlink on the registry row.
 *
 * Top-level collection: `jira_sources/{id}`. Cross-tenant; kb_admin RBAC.
 * Shared-corpus exception is implicit — the resulting documents land in the
 * same Ring 0 / Ring 1 collections that already carry the documented
 * exception in CLAUDE.md.
 *
 * Reuses the `jira_api` REST connector via the AI Agent Catalogue gateway
 * (NOT Rovo MCP). The crawl runs under a configured `syncIdentityEntraOid`
 * so it sees exactly what that user can see in Jira.
 */
export const JiraSyncStatus = z.enum(['idle', 'running', 'success', 'partial', 'failed']);
export const JiraSyncStats = z.object({
    issuesAttempted: z.number().int().nonnegative(),
    issuesSucceeded: z.number().int().nonnegative(),
    issuesFailed: z.number().int().nonnegative(),
    /** Issues whose stored jiraUpdatedAt matched Jira's current `updated` —
     *  delta-skipped without re-embedding. */
    issuesUnchanged: z.number().int().nonnegative().default(0),
    /** Total comments rendered into the ingested issues this run. */
    commentsIngested: z.number().int().nonnegative().default(0),
    /** Last error blob — first failure or summary. */
    error: z.string().optional(),
});
export const JiraSyncPhase = z.enum(['listing', 'fetching', 'ingesting', 'completed']);
export const JiraSyncIssueStatus = z.enum(['in_progress', 'succeeded', 'unchanged', 'failed']);
/**
 * Rolling per-issue log written alongside every syncProgress update.
 * Bounded to 20 entries (FIFO) so the UI can stream a "what just happened"
 * log without re-fetching anything else.
 */
export const JiraSyncIssueEntry = z.object({
    issueKey: z.string().min(1).max(60),
    summary: z.string().max(200),
    status: JiraSyncIssueStatus,
    /** ISO. Time of the row's last update. */
    ts: IsoTimestamp,
    durationMs: z.number().int().nonnegative().optional(),
    errorMessage: z.string().max(300).optional(),
});
/**
 * Live progress while a sync is in flight. Cleared/replaced on each new
 * sync. Frontend polls and renders a progress strip when present +
 * lastSyncStatus === 'running'.
 */
export const JiraSyncProgress = z.object({
    totalIssues: z.number().int().nonnegative(),
    currentIndex: z.number().int().nonnegative(),
    currentIssueKey: z.string().max(60).optional(),
    phase: JiraSyncPhase,
    issuesAttempted: z.number().int().nonnegative(),
    issuesSucceeded: z.number().int().nonnegative(),
    issuesFailed: z.number().int().nonnegative(),
    issuesUnchanged: z.number().int().nonnegative(),
    commentsIngested: z.number().int().nonnegative().default(0),
    startedAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    /**
     * Resume cursor for the next run. Jira's enhanced /search/jql endpoint
     * pages by opaque `nextPageToken`; we persist it so a re-run (manual or
     * scheduled) continues a large first crawl from where the 2h job ceiling
     * cut it off, mirroring Confluence's "partial — run again to continue".
     * Cleared when the crawl reaches the end of the result set.
     */
    nextPageToken: z.string().max(2000).nullable().optional(),
    recentIssues: z.array(JiraSyncIssueEntry).max(50).default([]).optional(),
});
export const JiraSource = z.object({
    id: DocId,
    name: z.string().min(1).max(100),
    /** e.g. `onenebula.atlassian.net` — host only, NO protocol. */
    cloudBaseUrl: z.string().min(1).max(120),
    /** Atlassian cloudId for 3LO routing. Stable per site. */
    cloudId: z.string().min(1).max(120),
    /** Jira project key, e.g. "OK". Upper-case alnum + underscore. */
    projectKey: z.string().regex(/^[A-Z][A-Z0-9_]+$/),
    ring: z.enum(['ring0', 'ring1']),
    /**
     * Whose gateway `jira_api` connection the crawl authenticates under. The
     * crawl sees exactly what this user can see in Jira. v1 uses an admin's
     * oid; move to a dedicated service/bot account before this is load-bearing.
     */
    syncIdentityEntraOid: z.string().min(1).max(200),
    lastSyncedAt: IsoTimestamp.optional(),
    lastSyncStatus: JiraSyncStatus.default('idle'),
    lastSyncStats: JiraSyncStats.optional(),
    syncProgress: JiraSyncProgress.optional(),
    createdAt: IsoTimestamp,
    createdBy: DocId,
    /**
     * ISO 27001 classification defaults that cascade to every issue ingested
     * from this project. Operator can downgrade per-doc post-ingest via the
     * Beheer Kennisbank PATCH route. Older sources without these read as
     * internal / []. A gated-but-ingested project is a config choice here (set
     * defaultClassification=confidential + an access group) with no code change.
     */
    ...sourceDefaultClassificationFields,
    /**
     * Specialisations applied to every issue ingested from this project so the
     * Knowledge Agent's chip filters and the Documents browse rail can scope by
     * topic. Names must exist in the `specialisations/` collection (validated at
     * write time by the admin route). Cap mirrors the 10-tag document cap.
     */
    specialisations: z.array(z.string().min(1).max(60)).max(10).default([]).optional(),
});
//# sourceMappingURL=jira-source.js.map